"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.grantableAccountListSchema = exports.roleAssignmentPreviewSchema = exports.roleAssignmentPreviewInputSchema = exports.roleAssignmentRevocationSchema = exports.roleAssignmentUpdateSchema = exports.roleAssignmentListSchema = exports.roleAssignmentRecordSchema = exports.ROLE_ASSIGNMENT_STATES = exports.roleAssignmentIdPathSchema = exports.roleAssignmentSchema = exports.roleAssignmentInputSchema = void 0;
// Granting a role, and the second approval some grants need (`RPM-093`, `J-270`).
const zod_1 = require("zod");
const rpm_enums_js_1 = require("./db/rpm-enums.js");
const rpm_blast_radius_js_1 = require("./rpm-blast-radius.js");
/**
 * One grant. The GRANTOR is not here: it is the session, and a body that could name it would be a
 * way round the self-grant rule. The tenant is not here either: it is the session's context
 * (`RPM-067`), so a grant can never land in a tenant the actor is not working in.
 */
exports.roleAssignmentInputSchema = zod_1.z
    .strictObject({
    /** The account the role is granted to. Never the actor's own (`RPM-093`). */
    userId: zod_1.z.uuid(),
    roleCode: zod_1.z.enum(rpm_enums_js_1.roleCode.enumValues),
    scopeKind: zod_1.z.enum(["TENANT", "PRACTICE", "CERTIFIED_FACILITY"]),
    /** The practice, for a `PRACTICE` grant. */
    scopeId: zod_1.z.uuid().optional(),
    /** The mirrored building, for a `CERTIFIED_FACILITY` grant. */
    scopeFacilityId: zod_1.z.string().min(1).max(20).optional(),
    /**
     * Until — when it expires (`RPM-094`), ISO 8601. Absent: it runs until it is changed or revoked.
     * From is not here: a grant starts when it is written, or when its second approval is given.
     */
    validTo: zod_1.z.iso.datetime({ offset: true }).optional(),
    /**
     * The blast radius the grant's confirm showed (`RPM-060`, `D-1544`): a grant changes who reaches
     * the buildings of its scope. Recorded on the grant's audit row as it arrived.
     */
    blastRadiusShown: rpm_blast_radius_js_1.blastRadiusShownSchema.optional(),
})
    .meta({ description: "A role granted to another account, at one scope" });
/**
 * The grant as held. `PENDING_SECOND_APPROVAL` grants nothing until a different account approves it;
 * `ACTIVE` is current reach.
 */
exports.roleAssignmentSchema = zod_1.z
    .object({
    roleAssignmentId: zod_1.z.uuid(),
    userId: zod_1.z.uuid(),
    roleCode: zod_1.z.enum(rpm_enums_js_1.roleCode.enumValues),
    scopeKind: zod_1.z.string(),
    scopeId: zod_1.z.uuid().nullable(),
    scopeFacilityId: zod_1.z.string().nullable(),
    grantedByUserId: zod_1.z.uuid(),
    status: zod_1.z.enum(["ACTIVE", "PENDING_SECOND_APPROVAL"]),
})
    .meta({
    id: "RoleAssignment",
    description: "A role grant, active or awaiting a second approver",
});
exports.roleAssignmentIdPathSchema = zod_1.z.object({ roleAssignmentId: zod_1.z.uuid() });
/**
 * Where an assignment stands now (`RPM-092`'s list, `RPM-094`'s states). `PENDING_SECOND_APPROVAL`
 * grants nothing yet; `SCHEDULED` starts at a From still to come; `EXPIRED` ended at its Until;
 * `REVOKED` was ended by an administrator.
 */
exports.ROLE_ASSIGNMENT_STATES = [
    "ACTIVE",
    "PENDING_SECOND_APPROVAL",
    "SCHEDULED",
    "EXPIRED",
    "REVOKED",
];
/**
 * One assignment as the Roles and permissions screen lists it (`RPM-092`, `J-296`). Staff accounts
 * only: a name and an address of a member of staff, never a resident. `mayChange` is the server's
 * answer for this actor — the screen states a withheld action in words rather than guessing.
 */
exports.roleAssignmentRecordSchema = zod_1.z
    .object({
    roleAssignmentId: zod_1.z.uuid(),
    userId: zod_1.z.uuid(),
    holderName: zod_1.z.string(),
    holderEmail: zod_1.z.string(),
    roleCode: zod_1.z.enum(rpm_enums_js_1.roleCode.enumValues),
    scopeKind: zod_1.z.string(),
    scopeId: zod_1.z.uuid().nullable(),
    scopeFacilityId: zod_1.z.string().nullable(),
    /** The building's name from the mirror, for a building grant; null otherwise. */
    scopeFacilityName: zod_1.z.string().nullable(),
    grantedByUserId: zod_1.z.uuid(),
    grantedByName: zod_1.z.string(),
    /** From, ISO 8601. Null while the grant waits for its second approver. */
    validFrom: zod_1.z.iso.datetime({ offset: true }).nullable(),
    /** Until, ISO 8601. Null when it runs until changed. */
    validTo: zod_1.z.iso.datetime({ offset: true }).nullable(),
    revokedAt: zod_1.z.iso.datetime({ offset: true }).nullable(),
    state: zod_1.z.enum(exports.ROLE_ASSIGNMENT_STATES),
    /** True when the actor may change its Until or revoke it: never for the actor's own. */
    mayChange: zod_1.z.boolean(),
    /** True when the actor may give its second approval: a different account, in scope. */
    mayApprove: zod_1.z.boolean(),
})
    .meta({ id: "RoleAssignmentRecord", description: "One role assignment, as the screen lists it" });
exports.roleAssignmentListSchema = zod_1.z
    .object({
    roleAssignments: zod_1.z.array(exports.roleAssignmentRecordSchema),
    /**
     * The tenant's default zone, which From and Until are shown and typed in, named beside the field
     * (`RPM-059`'s rule for a tenant-wide time, `D-1539`). Null outside a tenant.
     */
    timeZone: zod_1.z.string().nullable(),
})
    .meta({
    id: "RoleAssignmentList",
    description: "The tenant's role assignments the actor's own scope covers, newest first",
});
/**
 * Setting an assignment's Until (`RPM-094`): a new expiry, or `null` to let it run. An expiry is
 * behaviour-altering (access), so the change carries the counts its confirm showed (`D-1544`) and the
 * number of open alerts the warning named.
 */
exports.roleAssignmentUpdateSchema = zod_1.z
    .strictObject({
    validTo: zod_1.z.iso.datetime({ offset: true }).nullable(),
    blastRadiusShown: rpm_blast_radius_js_1.blastRadiusShownSchema,
    strandedAlertsShown: zod_1.z.number().int().nonnegative(),
    statedReason: zod_1.z.string().trim().min(1).max(500).optional(),
})
    .meta({ description: "A new Until for one assignment, with what its confirm showed" });
/** Revoking an assignment now: an expiry at this instant, with what its confirm showed. */
exports.roleAssignmentRevocationSchema = zod_1.z
    .strictObject({
    blastRadiusShown: rpm_blast_radius_js_1.blastRadiusShownSchema,
    strandedAlertsShown: zod_1.z.number().int().nonnegative(),
    statedReason: zod_1.z.string().trim().min(1).max(500).optional(),
})
    .meta({ description: "Ending one assignment now, with what its confirm showed" });
/**
 * What a grant or an expiry would reach, asked before its confirm (`RPM-060`, `RPM-094`, `D-1544`).
 * A grant names its scope; an expiry names the assignment and the instant it would end — its new
 * Until, or now for a revocation.
 */
exports.roleAssignmentPreviewInputSchema = zod_1.z
    .discriminatedUnion("kind", [
    zod_1.z.strictObject({
        kind: zod_1.z.literal("grant"),
        scopeKind: zod_1.z.enum(["TENANT", "PRACTICE", "CERTIFIED_FACILITY"]),
        scopeId: zod_1.z.uuid().optional(),
        scopeFacilityId: zod_1.z.string().min(1).max(20).optional(),
    }),
    zod_1.z.strictObject({
        kind: zod_1.z.literal("expiry"),
        roleAssignmentId: zod_1.z.uuid(),
        /** The instant it would end, ISO 8601; `null` asks about letting it run (nothing stranded). */
        endsAt: zod_1.z.iso.datetime({ offset: true }).nullable(),
    }),
])
    .meta({ description: "A grant's scope, or an assignment and when it would end" });
/**
 * The blast radius of the grant or expiry, and for an expiry the open alerts it would strand:
 * "3 alerts are open and would be stranded" (`RPM-094`'s Operator surface).
 */
exports.roleAssignmentPreviewSchema = rpm_blast_radius_js_1.blastRadiusSchema
    .extend({ strandedAlerts: zod_1.z.number().int().nonnegative() })
    .meta({
    id: "RoleAssignmentPreview",
    description: "What a grant or an expiry reaches, and the open alerts an expiry would strand",
});
/**
 * The Subject chooser's accounts (`RPM-093`'s Operator surface): staff accounts of this tenant, and
 * accounts no tenant has granted anything yet. `isSelf` marks the actor's own, which the chooser
 * shows disabled with the reason beside it.
 */
exports.grantableAccountListSchema = zod_1.z
    .object({
    accounts: zod_1.z.array(zod_1.z.object({
        userId: zod_1.z.uuid(),
        name: zod_1.z.string(),
        email: zod_1.z.string(),
        isSelf: zod_1.z.boolean(),
    })),
})
    .meta({ id: "GrantableAccountList", description: "The accounts a role may be granted to" });
//# sourceMappingURL=rpm-role-assignments.js.map